import { APITypesV1 } from "@cur8/api-client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAPIClient } from "render/context/APIContext";
import { useSession } from "render/context/MSALContext";
import { Typography } from "render/ui/presentation/Typography";
import LinkItem from "render/ui/trigger/LinkItem";
import { ListItem } from "render/ui/trigger/ListItem";
import ConsentToggle from "render/views/ProfileView/components/ConsentDocument/components/ConsentToggle";
import {
  ContentProps,
  sharedStyles,
} from "render/views/ProfileView/components/ConsentDocument/components/shared";
import { useStudyInfo } from "render/views/ProfileView/components/ConsentDocument/hooks/useStudyInfo";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

type StudyContentProps = {
  documentUrl: string;
} & ContentProps;

export function StudyContent({
  documentUrl,
  allowEdit,
  checked,
  busy,
  study,
  onClick,
}: StudyContentProps) {
  const studyInfo = useStudyInfo(documentUrl);
  const studyDetailsLink = studyInfo.studyLink();
  const api = useAPIClient();
  const { patientId } = useSession();

  const [ephemeralToken, setEphemeralToken] = useState<string>();

  const fetchEphemeralToken = useCallback(() => {
    api.patient
      .createEphermeralToken(
        { patientId },
        { requestMethod: APITypesV1.RequestMethod.RenderPatientConsent }
      )
      .result.then((response) => {
        setEphemeralToken(response.token);
      });
  }, [api.patient, patientId]);

  useEffect(() => {
    fetchEphemeralToken();
  }, [fetchEphemeralToken]);

  const consentPDFDownloadLink = useMemo(() => {
    if (!ephemeralToken) return;

    const url = api.consent.getRenderConsentDocumentURL({
      patientId,
      token: ephemeralToken,
      documentUri: documentUrl,
    });

    return url.toString();
  }, [api.consent, documentUrl, ephemeralToken, patientId]);

  return (
    <div className={sharedStyles.content}>
      <div className={sharedStyles.title}>
        <Typography variant="title-medium">
          <studyInfo.title />
        </Typography>
      </div>
      <ConsentToggle
        allowEdit={allowEdit}
        checked={checked}
        busy={busy}
        onClick={onClick}
        study={study}
      />

      <div>
        <div className={styles.studySection}>
          <Typography variant="paragraph-large">
            <Trans.Invitation />
          </Typography>
          <Typography variant="paragraph">
            <studyInfo.invitation />
          </Typography>
        </div>
        <div className={styles.studySection}>
          <Typography variant="paragraph-large">
            <Trans.WhatsThePurpose />
          </Typography>
          <Typography variant="paragraph">
            <studyInfo.purpose />
          </Typography>
        </div>

        <section>
          <div className={styles.sectionTitle}>
            <Typography variant="title-medium">
              <Trans.MoreInfo />
            </Typography>
          </div>
          <ListItem
            name={<Trans.FindMoreInfo />}
            onClick={() => window.open(studyDetailsLink, "_blank")}
            iconVariant="download"
          />
          {checked && ephemeralToken && (
            <>
              <div className={styles.consentBorder} />
              {consentPDFDownloadLink && (
                <LinkItem
                  name={<Trans.ConsentHistory />}
                  target="_blank"
                  href={consentPDFDownloadLink}
                  iconVariant="download"
                />
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
